/**
 * Field Tree Hook
 * Builds a tree structure of fields with relational fields as nested children
 * Based on reference: reference/app/src/composables/use-field-tree.ts
 */

import { useMemo, useCallback, useState } from 'react';
import { useFieldsStore, type Field } from '@/stores/fields';
import { useRelations } from '@/hooks/useRelations';

export interface FieldNode {
    name: string;
    field: string;
    collection: string;
    relatedCollection?: string;
    key: string;
    path: string;
    type: string;
    children?: FieldNode[];
    group?: boolean;
    disabled?: boolean;
    /** For M2M nodes: the junction FK field that must appear in the key path */
    m2mJunctionField?: string;
}

interface UseFieldTreeOptions {
    collection: string | null;
    disabledFields?: string[];
    includeRelations?: boolean;
    includeAliasFields?: boolean;
    filter?: (field: Field, parent?: FieldNode) => boolean;
}

export function useFieldTree({
    collection,
    disabledFields = [],
    includeRelations = true,
    includeAliasFields = false,
    filter = () => true,
}: UseFieldTreeOptions) {
    const { getFieldsForCollectionSorted } = useFieldsStore();
    const { data: relations = [] } = useRelations();
    const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

    // Get relation for a field
    const getRelationForField = useCallback((field: Field) => {
        // First try to find direct relation
        let relation = relations.find(
            (r) => r.collection === field.collection && r.field === field.field
        );

        // If not found, check if it's an O2M (inverse relation)
        if (!relation) {
            relation = relations.find(
                (r) => r.related_collection === field.collection && r.meta?.one_field === field.field
            );
        }

        // If still not found and field has foreign_key_table, it might be a M2O without explicit relation
        // In this case, we can infer the related collection from schema
        if (!relation && field.schema?.foreign_key_table) {
            // Create a synthetic relation for M2O fields
            return {
                collection: field.collection,
                field: field.field,
                related_collection: field.schema.foreign_key_table,
                meta: null,
                schema: null,
            } as any;
        }

        return relation;
    }, [relations]);

    // Get relation type and related collections
    const getRelationTypeAndRelatedCollections = useCallback((field: Field): {
        relationType: 'o2m' | 'm2o' | 'm2m' | 'm2a' | null;
        relatedCollections: string[];
        junctionCollection?: string;
        junctionField?: string;
    } => {
        const relation = getRelationForField(field);
        
        // If no relation found, check if field has foreign_key_table (M2O from schema)
        if (!relation) {
            if (field.schema?.foreign_key_table) {
                return { 
                    relationType: 'm2o', 
                    relatedCollections: [field.schema.foreign_key_table] 
                };
            }
            return { relationType: null, relatedCollections: [] };
        }

        // Check if it's M2M - has junction_field (check this first as it's more specific)
        // M2M: relation.related_collection === field.collection && relation.meta?.one_field === field.field && has junction_field
        if (relation.related_collection === field.collection &&
            relation.meta?.one_field === field.field &&
            relation.meta?.junction_field) {
            const junctionCollection = relation.collection;
            const junctionField = relation.meta.junction_field;

            // Resolve the actual related collection via the other side of the junction
            const otherSide = relations.find(
                (r) => r.collection === junctionCollection && r.field === junctionField
            );

            if (otherSide?.related_collection) {
                return {
                    relationType: 'm2m',
                    relatedCollections: [otherSide.related_collection],
                    junctionCollection,
                    junctionField,
                };
            }

            // Fallback: show junction collection if other side not found
            return {
                relationType: 'm2m',
                relatedCollections: [junctionCollection],
                junctionCollection,
            };
        }

        // Check if it's O2M - has one_collection and one_field, but no junction_field
        // O2M: relation.related_collection === field.collection && relation.meta?.one_field === field.field
        if (relation.related_collection === field.collection && 
            relation.meta?.one_field === field.field && 
            !relation.meta?.junction_field) {
            return { relationType: 'o2m', relatedCollections: [relation.meta.many_collection] };
        }

        // Check if it's M2O - has related_collection directly and collection/field match
        // M2O: relation.collection === field.collection && relation.field === field.field
        // OR: field has foreign_key_table in schema (even if no explicit relation)
        if ((relation.collection === field.collection && 
             relation.field === field.field && 
             relation.related_collection) ||
            (field.schema?.foreign_key_table && 
             !relation.meta?.one_collection)) {
            const relatedCollection = relation.related_collection || field.schema?.foreign_key_table;
            if (relatedCollection) {
                return { relationType: 'm2o', relatedCollections: [relatedCollection] };
            }
        }

        // Check if it's M2A - has one_allowed_collections
        if (relation.meta?.one_allowed_collections && relation.meta.one_allowed_collections.length > 0) {
            return {
                relationType: 'm2a',
                relatedCollections: relation.meta.one_allowed_collections
            };
        }

        // Fallback: if relation exists and has related_collection, assume M2O
        // This handles cases where relation structure might be slightly different
        if (relation.related_collection) {
            return { relationType: 'm2o', relatedCollections: [relation.related_collection] };
        }

        // Last resort: check schema for foreign_key_table
        if (field.schema?.foreign_key_table) {
            return { 
                relationType: 'm2o', 
                relatedCollections: [field.schema.foreign_key_table] 
            };
        }

        return { relationType: null, relatedCollections: [] };
    }, [getRelationForField, relations]);

    // Build a node from a field
    const makeNode = useCallback((field: Field, parent?: FieldNode): FieldNode | FieldNode[] => {
        const pathContext = parent?.path ? parent.path + '.' : '';

        // Handle group fields
        if (field.meta?.special?.includes('group')) {
            const node: FieldNode = {
                name: field.name,
                field: field.field,
                collection: field.collection,
                relatedCollection: undefined,
                key: parent ? parent.key : '',
                path: pathContext + field.field,
                group: true,
                type: field.type,
            };

            const children = getTree(field.collection, node);

            return {
                ...node,
                children,
            };
        }

        // If not including relations, return simple node
        if (!includeRelations) {
            return {
                name: field.name,
                field: field.field,
                collection: field.collection,
                relatedCollection: undefined,
                key: field.field,
                path: field.field,
                type: field.type,
            };
        }

        // Handle relational fields
        const { relationType, relatedCollections, junctionField } = getRelationTypeAndRelatedCollections(field);
        const keyContext = parent?.key ? parent.key + '.' : '';

        if (relatedCollections.length <= 1 && relationType !== 'm2a') {
            return {
                name: field.name,
                field: field.field,
                collection: field.collection,
                relatedCollection: relatedCollections[0],
                key: keyContext + field.field,
                path: pathContext + field.field,
                type: field.type,
                // Store junction FK so processNode can insert it into child key paths
                ...(relationType === 'm2m' && junctionField ? { m2mJunctionField: junctionField } : {}),
            };
        }

        // Handle M2A (many-to-any) - multiple related collections
        return relatedCollections.map((relatedCollection) => {
            return {
                name: `${field.name} (${relatedCollection})`,
                field: `${field.field}:${relatedCollection}`,
                collection: field.collection,
                relatedCollection: relatedCollection,
                key: keyContext + `${field.field}:${relatedCollection}`,
                path: pathContext + `${field.field}:${relatedCollection}`,
                type: field.type,
            };
        });
    }, [includeRelations, getRelationTypeAndRelatedCollections]);

    // Get tree for a collection
    const getTree = useCallback((collectionName: string | null, parent?: FieldNode): FieldNode[] | undefined => {
        if (!collectionName) return undefined;

        const allFields = getFieldsForCollectionSorted(collectionName)
            .filter(
                (field) =>
                    includeAliasFields ||
                    field.meta?.special?.includes('group') ||
                    (!field.meta?.special?.includes('alias') && !field.meta?.special?.includes('no-data'))
            )
            .filter((field) => filter(field, parent));

        // Filter top-level fields (not in groups unless parent is a group)
        const topLevelFields = allFields.filter((field) => {
            if (parent?.group === true) return field.meta?.group === parent?.field;
            return !field.meta?.group;
        });

        const fieldNodes = topLevelFields.flatMap((field) => makeNode(field, parent));

        return fieldNodes.length > 0 ? fieldNodes : undefined;
    }, [getFieldsForCollectionSorted, includeAliasFields, filter, makeNode]);

    // Build the tree list - recursively load children for all relational fields
    // Track visited paths to prevent infinite recursion
    const treeList = useMemo(() => {
        if (!collection) return [];

        const rootTree = getTree(collection) ?? [];

        // Recursively process nodes to load all nested relational fields
        const processNode = (node: FieldNode, visitedPaths = new Set<string>(), depth = 0): FieldNode => {
            // Prevent infinite recursion - limit depth
            if (depth > 4) {
                return node;
            }

            // Check for cycles using the full path
            if (visitedPaths.has(node.path)) {
                // Already visited this path - return without children to break cycle
                return { ...node, children: undefined };
            }

            // Add current path to visited set
            const newVisitedPaths = new Set(visitedPaths);
            newVisitedPaths.add(node.path);

            const hasRelatedCollection = !!node.relatedCollection;

            if (hasRelatedCollection && node.relatedCollection) {
                // For M2M nodes: the children should live at `alias.junctionFK.field` in the key,
                // even though we skip displaying the junction table level. Pass a synthetic parent
                // whose key already includes the junction FK so makeNode builds the right key paths.
                const parentForTree = node.m2mJunctionField
                    ? { ...node, key: `${node.key}.${node.m2mJunctionField}` }
                    : node;

                const children = getTree(node.relatedCollection, parentForTree);

                const processedChildren = children?.map(child => {
                    const branchVisited = new Set(newVisitedPaths);
                    return processNode(child, branchVisited, depth + 1);
                });

                return { ...node, children: processedChildren };
            }

            if (node.children) {
                // Recursively process existing children
                const processedChildren = node.children.map(child => 
                    processNode(child, newVisitedPaths, depth + 1)
                );
                return { ...node, children: processedChildren };
            }

            return node;
        };

        return rootTree.map(node => processNode(node));
    }, [collection, getTree]);

    // Toggle expansion of a field path
    const toggleExpand = useCallback((path: string) => {
        setExpandedPaths((prev) => {
            const next = new Set(prev);
            if (next.has(path)) {
                next.delete(path);
            } else {
                next.add(path);
            }
            return next;
        });
    }, []);

    // Mark disabled fields
    const treeListWithDisabled = useMemo(() => {
        const markDisabled = (node: FieldNode): FieldNode => {
            const disabled = node.group || disabledFields.includes(node.key);
            return {
                ...node,
                disabled,
                children: node.children?.map(markDisabled),
            };
        };

        return treeList.map(markDisabled);
    }, [treeList, disabledFields]);

    return {
        treeList: treeListWithDisabled,
        toggleExpand,
        expandedPaths,
    };
}

